import { AnchorButton, Button } from '@askable/ui/core/button';
import { DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from '@askable/ui/core/dropdown-menu';
import { cn } from '@askable/ui/lib/utils';
import { showMessages } from '@intercom/messenger-js-sdk';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'shared-utils';
import { useIntercomSurvey } from 'src/hooks/useIntercomSurvey';
import { useQuery } from 'urql';

import { menuItemClass, menuItemCollapsedClass } from '../MenuItem';
import { ResponsiveMenu } from '../ResponsiveMenu';
import { UnreadBadge } from '../UnreadBadge';

import { FeaturedCallout } from './components/FeaturedCallout';
import { NewFeaturesList } from './components/NewFeaturesList';
import { NewFeatureNotifications } from './data/NewFeatureNotifications.query';
import { getLastViewedAt, isDismissed, markAsDismissed, setLastViewedAt } from './utils/featuredNotificationStorage';

import type { Notification } from './types';

export const HelpAndSupport = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { t } = useTranslation();
  const startSurvey = useIntercomSurvey();
  const [isMounted, setIsMounted] = useState(false);
  const [lastViewedAt, setLastViewedAtState] = useState<number | null>(getLastViewedAt);
  const isMobile = useMediaQuery('(max-width: 639px)');

  const [{ data }] = useQuery({
    query: NewFeatureNotifications,
    variables: { is_active: true, limit: 3 },
    requestPolicy: 'network-only',
  });

  const featured = data?.newFeatureNotifications?.featured ?? null;
  const canShowPopover = !!featured && !isDismissed(featured._id);
  const [delayedReady, setDelayedReady] = useState(false);
  const [isFeaturedDismissed, setIsFeaturedDismissed] = useState(false);

  const showFeatured = canShowPopover && delayedReady && !isFeaturedDismissed;

  const dismissFeaturedFeature = () => {
    if (featured) {
      markAsDismissed(featured._id);
    }
    setIsFeaturedDismissed(true);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!canShowPopover) {
      setDelayedReady(false);
      return;
    }
    const id = setTimeout(() => setDelayedReady(true), 3000);
    return () => clearTimeout(id);
  }, [canShowPopover]);

  useEffect(() => {
    setIsFeaturedDismissed(false);
  }, [featured?._id]);

  const nodes: Notification[] = data?.newFeatureNotifications?.nodes ?? [];
  const hasNotifications = nodes.length > 0;
  const hasNewItems = hasNotifications && nodes.some(n => (n.created ?? 0) > (lastViewedAt ?? 0));

  const desktopContent = (
    <>
      <DropdownMenuItem asChild>
        <a href="https://help.askable.com" target="_blank" rel="noreferrer">
          {t('sections.navigation.viewHelpArticles')}
        </a>
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={e => {
          e.stopPropagation();
          startSurvey();
        }}
      >
        {t('sections.navigation.giveFeedback')}
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={e => {
          e.stopPropagation();
          showMessages();
        }}
      >
        {t('sections.navigation.contactSupport')}
      </DropdownMenuItem>

      {/* New feature notifications */}
      {hasNotifications ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel>{t('sections.newFeatureNotifications.whatsNew')}</DropdownMenuLabel>
          <NewFeaturesList notifications={nodes} isMobile={false} />
          <DropdownMenuItem asChild>
            <a href="https://www.askable.com/product-updates" target="_blank" rel="noreferrer">
              {t('sections.newFeatureNotifications.viewAllReleases')}
              <ArrowRight className="size-4" />
            </a>
          </DropdownMenuItem>
        </>
      ) : null}
    </>
  );

  const mobileContent = (
    <div className="flex flex-col p-1 pt-0">
      <AnchorButton variant="dropdown" width="full" href="https://help.askable.com" target="_blank" rel="noreferrer">
        {t('sections.navigation.viewHelpArticles')}
      </AnchorButton>
      <Button
        variant="dropdown"
        width="full"
        onClick={e => {
          e.stopPropagation();
          startSurvey();
        }}
      >
        {t('sections.navigation.giveFeedback')}
      </Button>
      <Button
        variant="dropdown"
        width="full"
        onClick={e => {
          e.stopPropagation();
          showMessages();
        }}
      >
        {t('sections.navigation.contactSupport')}
      </Button>

      {hasNotifications ? (
        <>
          <DropdownMenuSeparator />
          <NewFeaturesList notifications={nodes} isMobile={true} />
        </>
      ) : null}
    </div>
  );

  const trigger = (
    <button
      className={cn(menuItemClass, 'w-fit aria-expanded:bg-foreground/[0.065] aria-expanded:text-foreground', {
        [menuItemCollapsedClass]: isCollapsed,
      })}
    >
      {hasNewItems ? <UnreadBadge isCollapsed={true} unreadCount={1} /> : null}
      <div className="shrink-0 p-[2px]">
        <HelpCircle className="size-4" />
      </div>
    </button>
  );

  return (
    <div className="relative">
      <ResponsiveMenu
        id="help-and-support-dropdown"
        trigger={trigger}
        onOpenChange={open => {
          if (open) {
            const now = Date.now();
            setLastViewedAt(now);
            setLastViewedAtState(now);
            dismissFeaturedFeature();
          }
        }}
        title={t('sections.navigation.helpSupport')}
        align={isCollapsed ? 'end' : 'start'}
        side={isCollapsed ? 'right' : 'top'}
        contentClassName="w-60"
        sideOffset={isCollapsed ? 12 : 2}
        isCollapsed={isCollapsed}
      >
        {isMounted && isMobile ? mobileContent : desktopContent}
      </ResponsiveMenu>
      {showFeatured && featured ? (
        <FeaturedCallout item={featured} isCollapsed={isCollapsed} onDismiss={dismissFeaturedFeature} />
      ) : null}
    </div>
  );
};
